/**
 * Client for the **phase 2** (per-org) setup endpoints. Unlike
 * `@/lib/setup-api`, these run against the normal authed API surface,
 * so every call pins the active organization through the shared SDK
 * transport.
 */
import * as generatedOrgSetup from '@reflex/ui/client/generated/org-setup';
import {
  SecretsStatusResponseSchema,
  SandboxProviderHealthResponseSchema,
  type SecretsStatusResponse,
  type SandboxProviderHealthResponse,
} from '@reflex/shared';
import { z } from 'zod';

const SandboxSourceSchema = z.enum(['ui', 'env', 'unset']);

const OrgSetupStateResponseSchema = z.object({
  orgId: z.string(),
  name: z.string(),
  slug: z.string(),
  bootstrappedAt: z.number().nullable(),
  sandboxConfigured: z.boolean(),
  sandboxSource: SandboxSourceSchema,
  installedPluginNames: z.array(z.string()),
});

/**
 * Verified Runloop account returned by `saveSandboxProvider` after the
 * server checks the key against `GET /v1/accounts/me`. Surfaced in the
 * wizard so users can confirm which account the key belongs to.
 */
const RunloopAccountSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  tier: z.string(),
  tierLabel: z.string(),
});

const OrgSandboxProviderResponseSchema = z.object({
  provider: z.string(),
  label: z.string(),
  docsUrl: z.string().optional(),
  hasKey: z.boolean(),
  source: SandboxSourceSchema,
  /** Live account behind the stored key (`GET /v1/accounts/me`). */
  account: RunloopAccountSummarySchema.nullish(),
  /** Pinned Runloop account id — survives key deletion. */
  pinnedAccountId: z.string().nullish(),
});

/**
 * Shared by the two routes that bind a Runloop key to the org: saving a
 * pasted key and provisioning a fresh account.
 */
const BoundRunloopAccountSchema = z.object({
  ok: z.literal(true),
  account: RunloopAccountSummarySchema,
  pinnedAccountId: z.string().nullable(),
});

const CopyKeyMaterialResponseSchema = z.object({
  ok: z.literal(true),
  runloopKeyCopied: z.boolean(),
  modelKeysCopied: z.number(),
});

const BootstrapCompleteResponseSchema = z.object({
  ok: z.literal(true),
  bootstrappedAt: z.number().nullable(),
});

export type OrgSetupStateResponse = z.infer<typeof OrgSetupStateResponseSchema>;
export type OrgSandboxProviderResponse = z.infer<typeof OrgSandboxProviderResponseSchema>;
export type RunloopAccountSummary = z.infer<typeof RunloopAccountSummarySchema>;

// Phase 2 setup endpoints flip across a database reset (`bootstrappedAt`
// un-stamps, `sandboxConfigured` reverts, sandbox health drops to
// `missing`), so their responses must never be served from
// the browser cache — a stale "all done" payload otherwise wedges the setup
// gate past the wizard. The server also sends `cache-control: no-store`.
const NO_STORE = { cache: 'no-store' as const };
const orgRequest = (orgId: string) => ({ organizationId: orgId });
const noStoreOrgRequest = (orgId: string) => ({ ...NO_STORE, organizationId: orgId });

/**
 * Parse at the boundary. The generated clients are typed from an OpenAPI spec
 * that declares no response schema for these routes, so their return type is
 * effectively `unknown` — casting it would let a server shape change surface as
 * a confusing crash deep in a component instead of here. Every call goes
 * through a schema so a drifted payload fails at the edge with a readable path.
 */
async function parsed<S extends z.ZodType>(
  schema: S,
  result: Promise<unknown>,
): Promise<z.infer<S>> {
  return schema.parse(await result);
}

export const orgSetupApi = {
  getState: (orgId: string) =>
    parsed(
      OrgSetupStateResponseSchema,
      generatedOrgSetup.getOrgSetupState(noStoreOrgRequest(orgId)),
    ),

  getSandboxProvider: (orgId: string) =>
    parsed(
      OrgSandboxProviderResponseSchema,
      generatedOrgSetup.getSandboxProvider(noStoreOrgRequest(orgId)),
    ),

  /**
   * Whether the org's *stored* Runloop key actually works (vs merely being
   * set). Drives the setup gate's re-route of a bootstrapped org with a
   * revoked / missing / trial key back into the wizard's Runloop step.
   */
  getSandboxHealth: (orgId: string): Promise<SandboxProviderHealthResponse> =>
    parsed(
      SandboxProviderHealthResponseSchema,
      generatedOrgSetup.getSandboxProviderHealth(noStoreOrgRequest(orgId)),
    ),

  saveSandboxProvider: (orgId: string, apiKey: string) =>
    parsed(
      BoundRunloopAccountSchema,
      generatedOrgSetup.setSandboxProvider({ apiKey }, orgRequest(orgId)),
    ),

  provisionRunloopAccount: (orgId: string) =>
    parsed(
      BoundRunloopAccountSchema,
      generatedOrgSetup.provisionOrganizationRunloopAccount(orgRequest(orgId)),
    ),

  /**
   * Org-scoped secrets status read. Unlike `api.getSecretsStatus()` (which
   * hits `/admin/secrets/status` and is short-circuited to the unprefixed
   * host slot for super-admins), this route always scopes by the URL-bound
   * active org — so the wizard's "Ready" badges reflect the org's own slot.
   */
  getSecretsStatus: (orgId: string): Promise<SecretsStatusResponse> =>
    parsed(
      SecretsStatusResponseSchema,
      generatedOrgSetup.getOrgSecretsStatus(noStoreOrgRequest(orgId)),
    ),

  /**
   * Org-scoped secret write. The generic `api.setSecret()` is unsafe to use
   * during the org-setup wizard because `PUT /admin/secrets/:name` writes
   * to the unprefixed host slot whenever the caller is a super-admin or
   * single-tenant — see `secret.routes.ts` → `orgScope()`. This route
   * forces the write to `reflex/<orgId>/<name>` so `RunloopRegistry` and
   * the per-org devbox env-var refs can resolve the value.
   *
   * The response body is discarded — callers only care that it resolved.
   */
  saveSecret: async (orgId: string, name: string, value: string): Promise<void> => {
    await generatedOrgSetup.setOrgSecret(name, { value }, orgRequest(orgId));
  },

  /**
   * Copy the Runloop API key + every org-tier model provider key from
   * `sourceOrgId` into `orgId`. The caller must hold `secrets:manage` on
   * both orgs. Used by the create flow's optional "transfer keys from
   * another organization" path so a new org skips the Runloop step.
   */
  copyKeyMaterial: (orgId: string, sourceOrgId: string) =>
    parsed(
      CopyKeyMaterialResponseSchema,
      generatedOrgSetup.copyOrgKeyMaterial({ sourceOrgId }, orgRequest(orgId)),
    ),

  bootstrapComplete: (orgId: string) =>
    parsed(
      BootstrapCompleteResponseSchema,
      generatedOrgSetup.completeOrgBootstrap(orgRequest(orgId)),
    ),
};
